import React, { useState, useMemo } from 'react';

interface Column<T> {
  key: keyof T | string;
  header: string;
  render?: (item: T, index: number) => React.ReactNode;
  sortable?: boolean;
  width?: string;
}

interface PaginatedTableProps<T> {
  data: T[];
  columns: Column<T>[];
  pageSize?: number;
  loading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  onRowClick?: (item: T, index: number) => void;
  className?: string;
  emptyMessage?: string;
  actions?: (item: T, index: number) => React.ReactNode;
  showPageSizeSelector?: boolean;
  pageSizeOptions?: number[];
  maxHeight?: string;
  compact?: boolean;
  /**
   * Enables a leading checkbox column. Requires rowKey and onSelectionChange to
   * also be provided, otherwise selection is silently disabled.
   */
  selectable?: boolean;
  /** Stable unique identifier for a row. Required when selectable is true. */
  rowKey?: (item: T) => string;
  selectedKeys?: Set<string>;
  onSelectionChange?: (keys: Set<string>) => void;
  /**
   * Field name to use as the React row key when selection is not enabled.
   * Ignored when `selectable` + `rowKey` are supplied, since rowKey then provides
   * the stable identity.
   */
  keyField?: string;
  defaultSort?: { key: string; direction: 'asc' | 'desc' };
}

function PaginatedTable<T extends Record<string, any>>({
  data,
  columns,
  pageSize: initialPageSize = 10,
  loading = false,
  searchable = true,
  searchPlaceholder = "Search...",
  onRowClick,
  className = "",
  emptyMessage = "No data available",
  actions,
  showPageSizeSelector = true,
  pageSizeOptions = [5, 10, 15, 20, 25, 50],
  maxHeight = "600px",
  compact = false,
  selectable = false,
  rowKey,
  selectedKeys,
  onSelectionChange,
  keyField,
  defaultSort
}: PaginatedTableProps<T>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(defaultSort ?? null);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    if (!searchTerm) return data;
    
    return data.filter(item =>
      Object.values(item).some(value =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    if (!sortConfig) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!sortedData || !Array.isArray(sortedData)) return [];
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil((sortedData?.length || 0) / pageSize);

  // Selection is only active when the caller supplies everything needed to track it,
  // so existing call sites that don't pass these props are unaffected.
  const selectionEnabled = selectable && !!rowKey && !!onSelectionChange;
  const selection = selectedKeys ?? new Set<string>();

  // The header checkbox acts on every row matching the current search rather than
  // just the visible page, so "select all" behaves the way an admin expects when
  // they've filtered the list down first.
  const filteredKeys = useMemo(
    () => (selectionEnabled && rowKey ? sortedData.map(rowKey) : []),
    [selectionEnabled, rowKey, sortedData]
  );

  const selectedFilteredCount = filteredKeys.filter(key => selection.has(key)).length;
  const allFilteredSelected = filteredKeys.length > 0 && selectedFilteredCount === filteredKeys.length;
  const someFilteredSelected = selectedFilteredCount > 0 && !allFilteredSelected;

  const toggleRow = (key: string) => {
    if (!onSelectionChange) return;
    const next = new Set(selection);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onSelectionChange(next);
  };

  const toggleAllFiltered = () => {
    if (!onSelectionChange) return;
    const next = new Set(selection);
    if (allFilteredSelected) {
      filteredKeys.forEach(key => next.delete(key));
    } else {
      filteredKeys.forEach(key => next.add(key));
    }
    onSelectionChange(next);
  };

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const renderPagination = () => {
    if (totalPages <= 1 && !compact) return null;

    // Compact pagination (matches Download modal footer style)
    if (compact) {
      if (sortedData.length === 0) return null;
      return (
        <div className="px-5 py-2 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <p className="text-xs text-gray-600">
              Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span>–<span className="font-medium">{Math.min(currentPage * pageSize, sortedData.length)}</span> of <span className="font-medium">{sortedData.length}</span>
            </p>
            {showPageSizeSelector && (
              <div className="flex items-center space-x-1.5">
                <label className="text-xs text-gray-600">Show:</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="border border-gray-300 rounded text-xs px-1.5 py-0.5 bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {pageSizeOptions.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className="text-xs text-gray-600">per page</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-1.5">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-2.5 py-1 border border-gray-300 rounded text-xs bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-xs text-gray-600">Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-2.5 py-1 border border-gray-300 rounded text-xs bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      );
    }

    const pages = [];
    const maxVisiblePages = 7;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page button
    if (startPage > 1) {
      pages.push(
        <button
          key="first"
          onClick={() => handlePageChange(1)}
          className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
          title="First page"
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(
          <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
            ...
          </span>
        );
      }
    }

    // Previous button
    pages.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        title="Previous page"
      >
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </button>
    );

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
            i === currentPage
              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
          }`}
        >
          {i}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        title="Next page"
      >
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
        </svg>
      </button>
    );

    // Last page button
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
            ...
          </span>
        );
      }
      pages.push(
        <button
          key="last"
          onClick={() => handlePageChange(totalPages)}
          className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
          title="Last page"
        >
          {totalPages}
        </button>
      );
    }

    return (
      <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
        {/* Mobile pagination */}
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700 flex items-center">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>

        {/* Desktop pagination */}
        <div className="hidden sm:flex sm:items-center sm:justify-between">
          <div className="flex items-center space-x-4">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span>
                {' '}to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, sortedData.length)}
                </span>
                {' '}of{' '}
                <span className="font-medium">{sortedData.length}</span>
                {' '}results
              </p>
            </div>
            
            {showPageSizeSelector && (
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700">Show:</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="border border-gray-300 rounded-md text-sm px-2 py-1 bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {pageSizeOptions.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className="text-sm text-gray-700">per page</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Quick page jump */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">Go to:</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={currentPage}
                onChange={(e) => {
                  const page = Math.max(1, Math.min(totalPages, Number(e.target.value) || 1));
                  handlePageChange(page);
                }}
                className="w-16 border border-gray-300 rounded-md text-sm px-2 py-1 text-center focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              {pages}
            </nav>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-4 text-gray-600">Loading data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white shadow rounded-lg overflow-hidden ${className}`}>
      {/* Search Bar */}
      {searchable && (
        <div className={`${compact ? 'px-4 py-2.5' : 'px-6 py-4'} border-b border-gray-200`}>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page on search
              }}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder={searchPlaceholder}
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto" style={{ maxHeight }}>
        <div className="overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {selectionEnabled && (
                <th className={`${compact ? 'px-3 py-2' : 'px-6 py-3'} text-left`} style={{ width: '2.5rem' }}>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    ref={(el) => { if (el) el.indeterminate = someFilteredSelected; }}
                    onChange={toggleAllFiltered}
                    disabled={filteredKeys.length === 0}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                    aria-label={allFilteredSelected ? 'Deselect all matching rows' : 'Select all matching rows'}
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  className={`${compact ? 'px-3 py-2' : 'px-6 py-3'} text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                  }`}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(String(column.key))}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.header}</span>
                    {column.sortable && (
                      <svg
                        className={`h-4 w-4 ${
                          sortConfig?.key === column.key
                            ? 'text-gray-900'
                            : 'text-gray-400'
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        {sortConfig?.key === column.key && sortConfig.direction === 'desc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        )}
                      </svg>
                    )}
                  </div>
                </th>
              ))}
              {actions && (
                <th className={`${compact ? 'px-3 py-2' : 'px-6 py-3'} text-right text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (actions ? 1 : 0) + (selectionEnabled ? 1 : 0)}
                  className={`${compact ? 'px-3 py-8' : 'px-6 py-12'} text-center text-gray-500`}
                >
                  <div className="flex flex-col items-center">
                    <svg className="h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p className="text-lg font-medium">{emptyMessage}</p>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedData.map((item, index) => {
                // Row identity precedence: rowKey (selection) > keyField > list index.
                // rowKey wins because selection state is keyed on it and must stay
                // stable across sorting and pagination.
                const key = selectionEnabled && rowKey
                  ? rowKey(item)
                  : keyField
                    ? String(item[keyField])
                    : String(index);
                const isSelected = selectionEnabled && selection.has(key);

                return (
                <tr
                  key={key}
                  className={`${
                    onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''
                  } ${isSelected ? 'bg-blue-50' : ''}`}
                  onClick={() => onRowClick?.(item, (currentPage - 1) * pageSize + index)}
                >
                  {selectionEnabled && (
                    <td
                      className={`${compact ? 'px-3 py-2' : 'px-6 py-4'} whitespace-nowrap`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(key)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                        aria-label={`Select ${key}`}
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td key={String(column.key)} className={`${compact ? 'px-3 py-2' : 'px-6 py-4'} text-sm text-gray-900 ${column.render ? '' : 'whitespace-nowrap'}`}>
                      {column.render
                        ? column.render(item, (currentPage - 1) * pageSize + index)
                        : String(item[column.key] || '')
                      }
                    </td>
                  ))}
                  {actions && (
                    <td className={`${compact ? 'px-3 py-2' : 'px-6 py-4'} whitespace-nowrap text-right text-sm font-medium`}>                      {actions(item, (currentPage - 1) * pageSize + index)}
                    </td>
                  )}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
}

export default PaginatedTable;